import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useActivityTracking } from '../lib/activityTracking';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import TrialBanner from './TrialBanner';
import BillingSuggestionsPanel from './BillingSuggestionsPanel';
import { 
  LayoutDashboard, 
  Briefcase, 
  Search, 
  Menu, 
  LogOut, 
  User,
  Moon,
  Sun,
  ChevronDown,
  CreditCard,
  LayoutGrid,
  ChevronLeft,
  Clock,
  Timer
} from 'lucide-react';

const navItems = [
  { label: 'Dashboard', href: '/hub', icon: LayoutDashboard },
  { label: 'Cases', href: '/case-management', icon: Briefcase },
  { label: 'Search', href: '/search', icon: Search },
];

export default function Layout({ children }) {
  const { user, logout, getTrialDaysRemaining } = useAuth();
  const { isTracking } = useActivityTracking();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBillingPanel, setShowBillingPanel] = useState(false);
  
  // Get trial status
  const trialDays = getTrialDaysRemaining();
  const isOnTrial = user?.subscription_status === 'trial' && trialDays !== null;
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return false;
  });

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    if (newIsDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href) => {
    if (href === '/hub') return location.pathname === '/hub';
    if (href === '/case-management') return location.pathname.startsWith('/case-management');
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="layout">
      {/* Trial Banner */}
      <TrialBanner />

      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
        <div className="flex h-14 sm:h-16 items-center px-4 lg:px-8">
          {/* Back to Hub + Logo */}
          <div className="flex items-center gap-2 mr-4 sm:mr-8">
            <Link to="/hub" className="flex items-center" data-testid="back-to-hub-link">
              <Button variant="ghost" size="sm" className="h-9 px-2 text-muted-foreground hover:text-foreground">
                <ChevronLeft className="w-4 h-4" />
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </Link>
            <div className="h-6 w-px bg-border hidden sm:block" />
            <Link to="/dashboard" className="flex items-center gap-2 sm:gap-3" data-testid="logo-link">
              <img src="/logo.png" alt="Legal AIDynamics" className="w-8 h-8 sm:w-9 sm:h-9 rounded-md object-contain" />
              <span className="font-heading text-base sm:text-xl font-bold tracking-tight text-foreground hidden md:inline">
                Legal AIDynamics
              </span>
            </Link>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1 flex-1">
            {navItems.map((item) => (
              <Link key={item.href} to={item.href}>
                <Button
                  variant={isActive(item.href) ? 'secondary' : 'ghost'}
                  className={`font-body ${isActive(item.href) ? 'text-foreground' : 'text-muted-foreground'}`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-4 h-4 mr-2" />
                  {item.label}
                </Button>
              </Link>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-1 sm:gap-2 ml-auto">
            {/* Time Tracking Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowBillingPanel(true)}
              className="relative"
              data-testid="time-tracking-btn"
              title="AI Time Tracking"
            >
              <Timer className="w-5 h-5" />
              {isTracking && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="hidden sm:flex"
              data-testid="theme-toggle"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="font-body gap-2" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-navy/10 dark:bg-white/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-navy dark:text-white" />
                  </div>
                  <span className="hidden sm:inline max-w-32 truncate">
                    {user?.name || 'User'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="font-body text-sm font-medium text-foreground">{user?.name}</p>
                  <p className="font-body text-xs text-muted-foreground">{user?.email}</p>
                  {isOnTrial ? (
                    <Badge variant="outline" className="mt-1 text-xs">
                      <Clock className="w-3 h-3 mr-1" />
                      Trial: {trialDays} day{trialDays === 1 ? '' : 's'} left
                    </Badge>
                  ) : user?.subscription_status === 'active' && (
                    <Badge className="mt-1 text-xs bg-success">Pro Member</Badge>
                  )}
                </div>
                <DropdownMenuSeparator />
                {isOnTrial && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/subscribe')}>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Upgrade to Pro
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={toggleTheme} className="sm:hidden">
                  {isDark ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                  {isDark ? 'Light Mode' : 'Dark Mode'}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="sm:hidden" />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72">
                <div className="flex items-center gap-3 mb-4">
                  <img src="/logo.png" alt="Legal AIDynamics" className="w-9 h-9 rounded-md object-contain" />
                  <span className="font-heading text-xl font-bold tracking-tight text-foreground">
                    Legal AIDynamics
                  </span>
                </div>
                <Link 
                  to="/hub"
                  onClick={() => setMobileMenuOpen(false)}
                  className="mb-4 block"
                >
                  <Button variant="outline" className="w-full justify-start font-body text-muted-foreground">
                    <LayoutGrid className="w-4 h-4 mr-3" />
                    Back to Product Hub
                  </Button>
                </Link>
                <nav className="flex flex-col gap-2">
                  {navItems.map((item) => (
                    <Link 
                      key={item.href} 
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Button
                        variant={isActive(item.href) ? 'secondary' : 'ghost'}
                        className={`w-full justify-start font-body ${isActive(item.href) ? 'text-foreground' : 'text-muted-foreground'}`}
                      >
                        <item.icon className="w-4 h-4 mr-3" />
                        {item.label}
                      </Button>
                    </Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Billing Suggestions Panel */}
      <BillingSuggestionsPanel
        isOpen={showBillingPanel}
        onClose={() => setShowBillingPanel(false)}
      />
    </div>
  );
}
